
import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { MobileOptimizedSearchSection } from '@/components/home/MobileOptimizedSearchSection';
import { QuizSection } from '@/components/home/QuizSection';
import { EnhancedSEO } from '@/components/seo/EnhancedSEO';
import { StructuredData, createWebSiteSchema, createOrganizationSchema } from '@/components/seo/StructuredData';
import { trackElementTimings } from '@/lib/performance-monitor';
import { useImagePreload } from '@/components/context/ImagePreloadContext';
import { isMobileDevice } from '@/lib/lcp-optimization';
import { initMobileOptimizations } from '@/lib/mobile-performance';
import { useAuth } from '@/hooks/useAuth';

console.log('Index.tsx - Module loading');

// Lazy load less critical sections
const LazyPopularDestinations = lazy(() => 
  import('@/components/home/PopularDestinationsSection')
  .then(m => ({ default: m.PopularDestinationsSection }))
);
const LazyPopularHosts = lazy(() => 
  import('@/components/home/PopularHostsSection')
  .then(m => ({ default: m.PopularHostsSection }))
);
const LazyPopularCollections = lazy(() => 
  import('@/components/home/PopularCollectionsSection')
  .then(m => ({ default: m.PopularCollectionsSection }))
);

// Lightweight loading fallback
const LoadingFallback = () => (
  <div className="w-full h-16 bg-gray-100 rounded-lg below-fold"></div>
);

const Index = () => {
  console.log('Index component rendering - START');
  
  const { preloadLcpImage } = useImagePreload();
  const searchSectionRef = useRef<HTMLDivElement>(null);
  const isMobile = isMobileDevice();
  const { isAuthenticated } = useAuth();
  
  console.log('Index - Hooks initialized successfully');
  
  useEffect(() => {
    console.log('Index useEffect running');
    // Initialize mobile optimizations first
    initMobileOptimizations();
    
    // Track LCP element
    trackElementTimings('.search-section', 'Search Section LCP');
    
    // Preload critical images - use travel-focused image
    const lcpImage = isMobile 
      ? 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=640&h=480&fit=crop&auto=format&q=80'
      : 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=630&fit=crop&auto=format&q=80';
    preloadLcpImage(lcpImage);
    
    // Mark search section as LCP
    if (searchSectionRef.current) {
      searchSectionRef.current.setAttribute('data-lcp', 'true');
      searchSectionRef.current.classList.add('above-fold');
    }
    
    // Defer non-critical content on mobile
    if (isMobile) {
      const deferContent = () => {
        document.querySelectorAll('.below-fold').forEach(element => {
          if (element instanceof HTMLElement) {
            element.style.contentVisibility = 'auto';
            element.style.containIntrinsicSize = '1px 150px';
          }
        });
      };
      
      // Defer after initial render
      setTimeout(deferContent, 100);
    }
  }, [preloadLcpImage, isMobile]);

  // Create more detailed website schema with all properties
  const websiteSchema = createWebSiteSchema({
    name: "travilis - Your Ultimate Travel Companion",
    description: "Connect with local hosts, discover amazing destinations, and plan your perfect journey with personalized recommendations and expert guides.",
    keywords: ["travel", "destinations", "local hosts", "travel planning", "vacation", "tourism"],
    language: "en-US",
    copyrightYear: new Date().getFullYear(),
    lastModified: new Date().toISOString()
  });

  // Create enhanced organization schema with more details
  const organizationSchema = createOrganizationSchema({
    foundingDate: "2023",
    email: "contact@travilis.com",
    phone: "+1 (555) 123-4567",
    address: {
      street: "123 Travel Lane",
      city: "San Francisco",
      region: "CA",
      postal: "94103",
      country: "US"
    },
    socialProfiles: [
      "https://www.facebook.com/travilis",
      "https://twitter.com/travilis",
      "https://www.instagram.com/travilis/",
      "https://www.linkedin.com/company/travilis/",
      "https://www.pinterest.com/travilis/"
    ]
  });

  console.log('Index component about to render JSX');

  return (
    <div className="space-y-4 pb-8 md:space-y-6">
      <EnhancedSEO 
        title="Your Ultimate Travel Companion - Connect with Local Hosts Worldwide" 
        description="Discover dream destinations, connect with knowledgeable local hosts, and plan unforgettable journeys with personalized recommendations. Start your adventure today!"
        image="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=630&fit=crop&auto=format&q=80"
        keywords={["travel companion", "local hosts", "travel planning", "destinations", "vacation", "journey planning", "travel experiences"]}
        enableSearchBox={true}
      />
      
      {/* Add enhanced structured data directly */}
      <StructuredData schema={[websiteSchema, organizationSchema]} />
      
      <div className="container mx-auto px-4 grid md:grid-cols-5 gap-4 md:gap-6">
        {/* Optimized search section - LCP element */}
        <div 
          ref={searchSectionRef}
          className="md:col-span-3 lg:col-span-2 above-fold"
          data-priority="high"
          aria-label="Search travel destinations"
        >
          <MobileOptimizedSearchSection />
        </div>
        
        {/* Quiz section - above fold */}
        <div className="md:col-span-2 lg:col-span-3 above-fold">
          <QuizSection isLoggedIn={isAuthenticated} />
        </div>
      </div>
      
      {/* Lazy load below-the-fold content */}
      <div className="below-fold">
        <Suspense fallback={<LoadingFallback />}>
          <LazyPopularDestinations />
        </Suspense>
      </div>
      
      <div className="below-fold">
        <Suspense fallback={<LoadingFallback />}>
          <LazyPopularHosts />
        </Suspense>
      </div>
      
      <div className="below-fold">
        <Suspense fallback={<LoadingFallback />}>
          <LazyPopularCollections />
        </Suspense>
      </div>
    </div>
  );
};

console.log('Index component defined');
export default Index;
